/**
 * useClinics Hook
 *
 * Custom hook for fetching and managing clinic data across RPM components.
 * Provides consistent error handling and loading states.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNotify } from 'react-admin';
import { IClinic } from '../interfaces/clinic';
import { CommonService } from '../services/common';
import { ERROR_MESSAGES } from '../components/rpms/constants';

interface UseClinicsReturn {
  clinics: IClinic[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Fetches and manages clinic data with loading and error states
 *
 * @param options Configuration options
 * @param options.notifyOnError Whether to show error notifications (default: true)
 * @returns Clinic data, loading state, error state, and refetch function
 */
export function useClinics(options?: {
  notifyOnError?: boolean;
}): UseClinicsReturn {
  const { notifyOnError = true } = options || {};

  const [clinics, setClinics] = useState<IClinic[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const notify = useNotify();

  const fetchClinics = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const clinicsData = await CommonService.getClinics();
      setClinics(clinicsData || []);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch clinics');
      console.error('Error fetching clinics:', error);
      setError(error);

      if (notifyOnError) {
        notify(ERROR_MESSAGES.CLINIC_FETCH_FAILED, { type: 'error' });
      }
    } finally {
      setIsLoading(false);
    }
  }, [notify, notifyOnError]);

  useEffect(() => {
    fetchClinics();
  }, [fetchClinics]);

  return {
    clinics,
    isLoading,
    error,
    refetch: fetchClinics,
  };
}
